import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ml_access_token";
const REFRESH_KEY = "ml_refresh_token";

export const tokenStore = {
    get: () => {
        try { return window.localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
    },
    getRefresh: () => {
        try { return window.localStorage.getItem(REFRESH_KEY); } catch (_) { return null; }
    },
    set: (access, refresh) => {
        try {
            if (access) window.localStorage.setItem(TOKEN_KEY, access);
            if (refresh) window.localStorage.setItem(REFRESH_KEY, refresh);
        } catch (_) { /* ignore */ }
    },
    clear: () => {
        try {
            window.localStorage.removeItem(TOKEN_KEY);
            window.localStorage.removeItem(REFRESH_KEY);
        } catch (_) { /* ignore */ }
    },
};

export const api = axios.create({
    baseURL: `${BACKEND_URL}/api`,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// Attach Bearer token (primary auth path — iframe-safe). Cookies remain a
// same-origin fallback via withCredentials.
api.interceptors.request.use((config) => {
    const token = tokenStore.get();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

/**
 * Normalise a FastAPI error detail (string | {msg} | Pydantic error array)
 * into a single human-readable string. Returns null when there's nothing
 * to format, so callers can `formatApiErrorDetail(detail) || fallback`.
 */
export function formatApiErrorDetail(detail) {
    if (detail == null) return null;
    if (typeof detail === "string") return detail || null;
    if (Array.isArray(detail)) {
        const out = detail
            .map((e) => {
                if (e && typeof e.msg === "string") {
                    const where = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(".") : "";
                    return where ? `${where}: ${e.msg}` : e.msg;
                }
                return JSON.stringify(e);
            })
            .filter(Boolean);
        return out.length ? out.join(" · ") : null;
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    const s = String(detail);
    return s && s !== "[object Object]" ? s : null;
}

/**
 * Build a human-readable error message from any axios error, with smart
 * defaults for the common failure modes (network down, timeout, 5xx, etc).
 * Use this instead of `formatApiErrorDetail` when you want a fallback for
 * the no-response case.
 */
export function describeApiError(err, fallback = "Something went wrong. Please try again.") {
    if (!err) return fallback;
    // Server responded with an error
    if (err.response) {
        const status = err.response.status;
        const detail = formatApiErrorDetail(err.response.data?.detail);
        if (detail) return detail;
        if (status === 401) return "Wrong email or password.";
        if (status === 403) return "You don't have permission to do that.";
        if (status === 404) return "Not found.";
        if (status === 409) return "That conflicts with existing data.";
        if (status === 422) return "Some fields were invalid.";
        if (status === 429) return "Too many requests — please wait and try again.";
        if (status >= 500) return `Server error (${status}). Please try again in a moment.`;
        return `Request failed (${status}).`;
    }
    // Request was made but no response — network error / backend down / CORS
    if (err.request) {
        if (err.code === "ECONNABORTED") return "The server took too long to respond. Is the backend running?";
        return "Can't reach the server. Check your connection or that the backend is running.";
    }
    // Setup error
    return err.message || fallback;
}
